import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import React from 'react';
import { useDispatch } from 'react-redux';

import allActions from '../../../../../../actions';
import { TrademateAPI } from '../../../../../../API/trademate';
import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import UnoButton from '../../../../../utils/UnoButton';

const useStyles = makeStyles({
    section: {
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: unoTheme.defaultTextColor,
        marginBottom: '8px',
    },
    readyBox: {
        border: 'solid 1px ' + unoTheme.successColor,
        background: '#EFFBF0',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    empty: {
        fontSize: '13px',
        color: unoTheme.secondaryColor,
    },
    mainBadge: {
        color: unoTheme.successColor,
        fontWeight: 600,
        fontSize: '11px',
    },
    actions: {
        display: 'flex',
        gap: '8px',
        marginTop: '8px',
    },
});

function maskCnpj(value) {
    const digits = value ? String(value).replace(/[^\d]+/g, '') : '';
    if (digits.length !== 14) return digits;
    return digits.substring(0, 2) + '.' + digits.substring(2, 5) + '.' + digits.substring(5, 8)
        + '/' + digits.substring(8, 12) + '-' + digits.substring(12, 14);
}

/**
 * Etapa 3 — Confirmação.
 *
 * Mostra o resumo do que será enviado e as pendências que o backend apontou. A lista de pendências
 * vem do servidor de propósito: é exatamente a mesma checagem que o envio faz, então não há como a
 * tela liberar um botão que o backend recusaria.
 */
export default function TrademateStep3Confirmation(props) {

    const { clientId, canReopen, data, onChanged } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const registration = data && data.registration ? data.registration : null;
    const isSent = registration && registration.status === 'SENT';

    const alert = (type, message) => dispatch(allActions.mainAlertActions.setMainAlert({
        show: true, type, message
    }));

    const doReopen = async () => {

        const reason = window.prompt(
            'Reabrir o cadastro devolve a edição ao RPPS. Motivo (opcional):', '');
        //prompt devolve null quando o usuário cancela; string vazia é um motivo em branco válido
        if (reason === null) return;

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Reabrindo...' }));
        const response = await TrademateAPI.reopen(clientId, reason);
        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));

        if (response.success) {
            alert('success', 'Cadastro reaberto');
            onChanged();
        } else {
            alert('error', response.body && response.body.message
                ? response.body.message : 'Erro ao reabrir o cadastro');
        }
    };

    //`withAccount` liga a coluna Conta B3, que só existe para custodiante. O resumo tem que
    //mostrar exatamente o que vai ser enviado.
    const renderInstitutions = (title, list, withAccount) => (
        <div className={classes.section}>
            <Typography align='left' className={classes.sectionTitle}>{title}</Typography>
            {list.length === 0 ? (
                <Typography className={classes.empty}>{'Nenhum informado.'}</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Razão Social</TableCell>
                            <TableCell align="left" style={{ width: '190px' }}>CNPJ</TableCell>
                            {withAccount && (
                                <TableCell align="left" style={{ width: '150px' }}>Conta B3</TableCell>
                            )}
                            <TableCell align="center" style={{ width: '100px' }}>Principal</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.map((item) => (
                            <TableRow key={title + '-' + item.id}>
                                <TableCell align="left">{item.name}</TableCell>
                                <TableCell align="left">{maskCnpj(item.cnpj)}</TableCell>
                                {withAccount && (
                                    <TableCell align="left">
                                        {item.account_number ? item.account_number : '-'}
                                    </TableCell>
                                )}
                                <TableCell align="center">
                                    {item.is_main
                                        ? <span className={classes.mainBadge}>Principal</span>
                                        : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );

    if (!registration) {
        return <Typography className={classes.empty}>
            {'Salve os dados da etapa 1 para continuar.'}
        </Typography>;
    }

    return (
        <div>

            {/* Esta etapa é o recibo: o envio acontece no rodapé da Etapa 2. Só a LEMA age aqui,
                reabrindo o cadastro. */}
            <div className={classes.readyBox}>
                <CheckCircleIcon style={{ color: unoTheme.successColor, fontSize: '18px' }} />
                {isSent
                    ? 'Cadastro enviado para a LEMA. Abaixo, o que foi submetido.'
                    : 'Cadastro reaberto para edição. Abaixo, o que havia sido submetido.'}
            </div>

            {renderInstitutions('Custodiantes', data.custodians, true)}
            {renderInstitutions('Liquidantes', data.settlements, false)}

            <div className={classes.section}>
                <Typography align='left' className={classes.sectionTitle}>{'Gestores indicados'}</Typography>
                {data.managers.length === 0 ? (
                    <Typography className={classes.empty}>{'Nenhum gestor indicado.'}</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Nome</TableCell>
                                <TableCell align="left" style={{ width: '260px' }}>E-mail</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.managers.map((manager) => (
                                <TableRow key={'manager-' + manager.user_id}>
                                    <TableCell align="left">{manager.name}</TableCell>
                                    <TableCell align="left">{manager.email}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <div className={classes.section}>
                <Typography align='left' className={classes.sectionTitle}>{'Documentos'}</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Documento</TableCell>
                            <TableCell align="left">Arquivo assinado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.documents.filter(d => !d.complementary).map((document) => (
                            <TableRow key={'doc-' + document.document_code}>
                                <TableCell align="left">{document.name}</TableCell>
                                <TableCell align="left">
                                    {document.signed_file_name ? document.signed_file_name : 'Pendente'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className={classes.section}>
                <Typography align='left' className={classes.sectionTitle}>
                    {'Documentos complementares'}
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Documento</TableCell>
                            <TableCell align="left">Arquivos anexados</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.documents.filter(d => d.complementary).map((document) => (
                            <TableRow key={'doc-' + document.document_code}>
                                <TableCell align="left">{document.name}</TableCell>
                                <TableCell align="left">
                                    {document.files.length > 0
                                        ? document.files.map(f => f.file_name).join(', ')
                                        : 'Pendente'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className={classes.actions}>

                {canReopen && (
                    <UnoButton
                        size='small'
                        type='warning'
                        startIcon={<LockOpenIcon />}
                        onClick={doReopen}>
                        {'Reabrir cadastro'}
                    </UnoButton>
                )}

            </div>

        </div>
    );
}
